// ============================================================================
// RunSheetMenu and RunSheetPopover: the Run Sheet toolbar's floating layers.
// ----------------------------------------------------------------------------
// Lucid 009 (2026-09-26) folded the lane chips, the Status and Labels
// dropdowns and the sort and tools row into one toolbar row, so the View menu,
// the sort menu, the overflow menu and the Columns panel all float over the
// sheet. They share the mechanics here, and every one of them is a rule this
// repo already paid for (docs/design-system.md HARD RULE 6):
//
//   * PORTAL to <body>, position: fixed, clamped to the viewport. The toolbar
//     card and the sheet are clipping ancestors; an inline popover is cut off
//     at their edge.
//   * RE-ANCHORED on scroll (capture phase, ignoring scrolls that start inside
//     the layer itself) and on resize, rather than left floating where the
//     trigger used to be.
//   * The usable floor is the TOP of the bottom bar when it is showing, so a
//     long menu scrolls inside itself instead of running under the bar.
//   * Click outside closes (pointerdown and mousedown, both idempotent, so a
//     touch that never synthesises a mouse event still closes it).
//   * Keys are taken at the WINDOW CAPTURE phase while a layer is open. The
//     page has window-level hotkeys (j/k and the arrows walk rows, Enter opens
//     the focused row, 1-5 switch lanes, Escape closes the preview), and a key
//     meant for a menu must never also drive the sheet underneath it.
// ============================================================================

import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export const MENU_GAP = 4;
export const VIEWPORT_MARGIN = 8;
/** Below this much room under the trigger, open upwards if above is roomier. */
const MIN_USEFUL_HEIGHT = 180;

/** Exactly one of top/bottom is set: an upward menu is anchored by its BOTTOM
 *  edge, so it hugs the trigger whatever its height. */
export interface MenuPlacement {
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
}

interface AnchorRect { top: number; bottom: number; left: number; right: number }

/**
 * Where a floating layer goes. Pure, so the clamp is testable without layout.
 *
 * `floor` is the lowest usable y (the top of the bottom bar when it shows).
 * The width never exceeds the viewport less a margin on each side, and the
 * left edge is clamped so the whole layer stays on screen.
 */
export function placeMenu(
    anchor: AnchorRect,
    viewport: { width: number; height: number },
    opts: { width: number; align?: 'start' | 'end'; floor?: number },
): MenuPlacement {
    const vw = viewport.width;
    const vh = viewport.height;
    const floor = Math.min(vh, opts.floor ?? vh);
    const width = Math.max(0, Math.min(opts.width, vw - VIEWPORT_MARGIN * 2));
    let left = opts.align === 'end' ? anchor.right - width : anchor.left;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - VIEWPORT_MARGIN - width));
    const below = floor - anchor.bottom - MENU_GAP - VIEWPORT_MARGIN;
    const above = anchor.top - MENU_GAP - VIEWPORT_MARGIN;
    if (below < MIN_USEFUL_HEIGHT && above > below) {
        return { bottom: vh - anchor.top + MENU_GAP, left, width, maxHeight: Math.max(0, above) };
    }
    return { top: anchor.bottom + MENU_GAP, left, width, maxHeight: Math.max(0, below) };
}

/** The top of the bottom bar when it is on screen, else the viewport bottom. */
function usableFloor(): number {
    const vh = window.innerHeight;
    const bar = document.querySelector('.qm-bottom-nav') as HTMLElement | null;
    if (!bar) return vh;
    const r = bar.getBoundingClientRect();
    return r.height > 0 && r.top > 0 && r.top < vh ? r.top : vh;
}

/** Placement that follows its anchor while open. Null while closed. */
export function useAnchoredPlacement(
    open: boolean,
    anchorRef: React.MutableRefObject<HTMLElement | null>,
    layerRef: React.MutableRefObject<HTMLElement | null>,
    width: number,
    align: 'start' | 'end',
): MenuPlacement | null {
    const [pos, setPos] = useState<MenuPlacement | null>(null);
    const place = useCallback(() => {
        const el = anchorRef.current;
        if (!el || typeof window === 'undefined') return;
        const r = el.getBoundingClientRect();
        setPos(placeMenu(r, { width: window.innerWidth, height: window.innerHeight }, { width, align, floor: usableFloor() }));
    }, [anchorRef, width, align]);

    useLayoutEffect(() => {
        if (open) place();
        else setPos(null);
    }, [open, place]);

    useEffect(() => {
        if (!open) return undefined;
        const onScroll = (e: Event) => {
            const t = e.target as Node | null;
            if (t && layerRef.current?.contains(t)) return;
            place();
        };
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', place);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', place);
        };
    }, [open, place, layerRef]);

    return pos;
}

const ITEM_SELECTOR = '[role="menuitem"],[role="menuitemradio"],[role="menuitemcheckbox"]';

/** The items the arrow keys walk, in DOM order, skipping disabled ones. */
export function menuItems(root: HTMLElement | null): HTMLElement[] {
    if (!root) return [];
    return Array.from(root.querySelectorAll<HTMLElement>(ITEM_SELECTOR)).filter(
        el => !(el as HTMLButtonElement).disabled && el.getAttribute('aria-disabled') !== 'true',
    );
}

/** Close on a press outside both the anchor and the layer. */
function useOutsidePress(
    open: boolean,
    anchorRef: React.MutableRefObject<HTMLElement | null>,
    layerRef: React.MutableRefObject<HTMLElement | null>,
    onOutside: () => void,
) {
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e: Event) => {
            const t = e.target as Node | null;
            if (t && (anchorRef.current?.contains(t) || layerRef.current?.contains(t))) return;
            onOutside();
        };
        document.addEventListener('pointerdown', onDown, true);
        document.addEventListener('mousedown', onDown, true);
        return () => {
            document.removeEventListener('pointerdown', onDown, true);
            document.removeEventListener('mousedown', onDown, true);
        };
    }, [open, anchorRef, layerRef, onOutside]);
}

export interface RunSheetMenuProps {
    /** The menu's accessible name. */
    label: string;
    /** Preferred width in px; clamped to the viewport. */
    width: number;
    align?: 'start' | 'end';
    /** Extra class on the floating menu. */
    className?: string;
    triggerClassName: string;
    triggerAriaLabel?: string;
    triggerTitle?: string;
    /** Extra attributes for the trigger (data-*, aria-busy). */
    triggerProps?: Record<string, unknown>;
    triggerContent: React.ReactNode;
    /** Lets the owner focus the trigger or anchor another layer to it. */
    triggerRef?: React.MutableRefObject<HTMLButtonElement | null>;
    /** Rendered inside the menu. `close(true)` returns focus to the trigger. */
    children: (close: (returnFocus?: boolean) => void) => React.ReactNode;
}

/**
 * A menu button (WAI-ARIA APG menu button pattern) whose menu is portalled.
 *
 * Focus moves INTO the menu when it opens: onto the checked radio item when
 * there is one, else the first item. Arrow keys, Home and End walk the items
 * with real focus (items are buttons, so Enter and Space activate them
 * natively). Escape and Tab close and return focus to the trigger. The
 * children decide whether an item closes the menu: a radio pick closes it, a
 * checkbox toggle leaves it open so several can be ticked in one visit.
 */
export default function RunSheetMenu({
    label, width, align = 'start', className, triggerClassName, triggerAriaLabel, triggerTitle,
    triggerProps, triggerContent, triggerRef, children,
}: RunSheetMenuProps) {
    const [open, setOpen] = useState(false);
    const ownTriggerRef = useRef<HTMLElement | null>(null);
    const menuRef = useRef<HTMLElement | null>(null);
    const menuId = useId();
    const pos = useAnchoredPlacement(open, ownTriggerRef, menuRef, width, align);

    const setTrigger = useCallback((el: HTMLButtonElement | null) => {
        ownTriggerRef.current = el;
        if (triggerRef) triggerRef.current = el;
    }, [triggerRef]);

    const close = useCallback((returnFocus = true) => {
        setOpen(false);
        if (returnFocus) { try { ownTriggerRef.current?.focus(); } catch { /* detached */ } }
    }, []);
    const closeQuietly = useCallback(() => close(false), [close]);

    useOutsidePress(open, ownTriggerRef, menuRef, closeQuietly);

    // Focus in once per open, when the menu first has a place. Keyed on the
    // boolean, not the placement object, so a re-anchor on scroll never pulls
    // focus back to the first item.
    const placed = pos !== null;
    useEffect(() => {
        if (!open || !placed) return;
        const items = menuItems(menuRef.current);
        const start = items.find(el => el.getAttribute('role') === 'menuitemradio' && el.getAttribute('aria-checked') === 'true')
            || items[0];
        try { start?.focus(); } catch { /* detached */ }
    }, [open, placed]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e: KeyboardEvent) => {
            const menu = menuRef.current;
            const t = e.target as Node | null;
            const inside = Boolean(t && menu?.contains(t));
            if (e.key === 'Escape' || e.key === 'Tab') {
                e.preventDefault();
                e.stopPropagation();
                close(true);
                return;
            }
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End') {
                e.preventDefault();
                e.stopPropagation();
                const items = menuItems(menu);
                if (!items.length) return;
                const at = inside ? items.indexOf(document.activeElement as HTMLElement) : -1;
                let next: number;
                if (e.key === 'Home') next = 0;
                else if (e.key === 'End') next = items.length - 1;
                else if (at === -1) next = e.key === 'ArrowDown' ? 0 : items.length - 1;
                else next = (at + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
                items[next]?.focus();
                return;
            }
            // Every other key typed inside the menu stays in the menu. Enter
            // and Space still activate the focused item: stopping propagation
            // does not cancel the button's default action.
            if (inside) e.stopPropagation();
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [open, close]);

    return (
        <>
            <button
                ref={setTrigger}
                type="button"
                className={triggerClassName}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                aria-label={triggerAriaLabel}
                title={triggerTitle}
                {...(triggerProps || {})}
                onClick={() => (open ? close(true) : setOpen(true))}
            >
                {triggerContent}
            </button>
            {open && pos && typeof document !== 'undefined' && createPortal(
                <div
                    ref={(el) => { menuRef.current = el; }}
                    id={menuId}
                    role="menu"
                    aria-label={label}
                    className={`rs-portal rs-menu${className ? ` ${className}` : ''}`}
                    style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
                >
                    {children(close)}
                </div>,
                document.body,
            )}
        </>
    );
}

export interface RunSheetPopoverProps {
    open: boolean;
    /** The element the panel hangs from; focus returns to it on Escape. */
    anchorRef: React.MutableRefObject<HTMLElement | null>;
    /** Called on Escape (after focus is handed back to the anchor) and on a
     *  press outside the panel. */
    onClose: () => void;
    /** The dialog's accessible name. */
    label: string;
    width: number;
    align?: 'start' | 'end';
    className?: string;
    children: React.ReactNode;
}

/**
 * A non-modal panel hung from an anchor: the Columns panel, opened from the
 * overflow menu. It holds form controls (checkboxes, lock toggles, a reset
 * button), so it is a dialog, not a menu. Focus moves to its first control on
 * open; Escape closes it and returns focus to the anchor.
 */
export function RunSheetPopover({
    open, anchorRef, onClose, label, width, align = 'end', className, children,
}: RunSheetPopoverProps) {
    const layerRef = useRef<HTMLElement | null>(null);
    const pos = useAnchoredPlacement(open, anchorRef, layerRef, width, align);
    useOutsidePress(open, anchorRef, layerRef, onClose);

    const placed = pos !== null;
    useEffect(() => {
        if (!open || !placed) return;
        const first = layerRef.current?.querySelector<HTMLElement>('input, button, [tabindex]:not([tabindex="-1"])');
        try { first?.focus(); } catch { /* detached */ }
    }, [open, placed]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                onClose();
                try { anchorRef.current?.focus(); } catch { /* detached */ }
                return;
            }
            const t = e.target as Node | null;
            if (t && layerRef.current?.contains(t)) e.stopPropagation();
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [open, onClose, anchorRef]);

    if (!open || !pos || typeof document === 'undefined') return null;
    return createPortal(
        <div
            ref={(el) => { layerRef.current = el; }}
            role="dialog"
            aria-modal="false"
            aria-label={label}
            className={`rs-portal rs-popover${className ? ` ${className}` : ''}`}
            style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
        >
            {children}
        </div>,
        document.body,
    );
}
